import { useAuth } from '../AuthContext';

/**
 * UID that the data providers should hold Firestore listeners open for, or
 * null when no listeners may be active (signed out / anonymous visitor).
 *
 * Only real (non-anonymous) users get listeners. Every visitor gets an
 * anonymous account (see AuthContext), but anonymous users only see the
 * pre-login entry — they have no data and consume none of these contexts.
 * Subscribing for them wastes Firestore reads and, when the anonymous account
 * converts to a different UID (e.g. Google sign-in), the stale
 * anonymous-scoped listeners fire "Missing or insufficient permissions"
 * errors as the auth token swaps.
 *
 * Returns a plain string (value-based) rather than the User object:
 * linkWithCredential (email signup from the anonymous account) mutates the
 * SAME User object in place and never fires onAuthStateChanged, so an effect
 * depending on the User reference would skip re-subscribing when isAnonymous
 * flips false. This hook recomputes on every render (the post-link re-render
 * is guaranteed by setIsNewSignup(true) in AuthContext.signup), so effects
 * with `[uid]` deps re-run exactly when the subscribable identity changes.
 */
export const useLiveDataUid = (): string | null => {
    const { currentUser } = useAuth();
    return currentUser && !currentUser.isAnonymous ? currentUser.uid : null;
};
